import { Component, ChangeDetectionStrategy, ChangeDetectorRef, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA, Input, OnChanges, SimpleChanges, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormControl, ReactiveFormsModule } from '@angular/forms';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { finalize } from 'rxjs/operators';

interface Document {
  id: string;
  name: string;
  type: string;
  size?: number;
  uploadDate: Date | string;
  url?: string;
  file?: File;
  sectionType: 'proyecto' | 'inmueble' | 'obra';
}

@Component({
  selector: 'app-new-project-documents',
  templateUrl: './new-project-documents.component.html',
  styleUrls: ['./new-project-documents.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
})
export class NewProjectDocumentsComponent implements OnInit, OnChanges {
  @Input() propertyForm: FormGroup | null = null;
  @Input() projectId: string | null = null;

  documents: { [key: string]: Document[] } = {
    proyecto: [],
    inmueble: [],
    obra: []
  };

  uploading: { [key: string]: number } = {
    proyecto: 0,
    inmueble: 0,
    obra: 0
  };

  constructor(
    private cdr: ChangeDetectorRef,
    private storage: AngularFireStorage
  ) {}

  ngOnInit(): void {
    this.loadDocumentsFromPropertyForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['propertyForm'] && !changes['propertyForm'].firstChange) {
      this.loadDocumentsFromPropertyForm();
    }
  }

  addDocument(sectionType: 'proyecto' | 'inmueble' | 'obra'): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.doc,.docx,.txt,.jpg,.jpeg,.png';
    input.multiple = true;
    
    input.onchange = (event: any) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        this.uploading[sectionType] = files.length;
        this.cdr.detectChanges();
        
        for (let file of files) {
          this.uploadFile(file, sectionType);
        }
      }
    };
    
    input.click();
  }

  private uploadFile(file: File, sectionType: 'proyecto' | 'inmueble' | 'obra'): void {
    if (!this.projectId) {
      console.error('No hay projectId disponible para subir el archivo');
      return;
    }

    const sanitizedName = this.sanitizeFileName(file.name);
    const filePath = `investments/${this.projectId}/documents/${sectionType}/${sanitizedName}`;
    const fileRef = this.storage.ref(filePath);
    const task = this.storage.upload(filePath, file);

    task.snapshotChanges().pipe(
      finalize(() => {
        fileRef.getDownloadURL().subscribe((url: string) => {
          const newDocument: Document = {
            id: this.generateId(),
            name: file.name,
            type: this.getFileType(file.name),
            size: file.size,
            uploadDate: new Date().toISOString(),
            url: url,
            sectionType: sectionType
          };
          
          this.documents[sectionType].push(newDocument);
          this.saveDocumentsToPropertyForm();
          this.uploading[sectionType] = Math.max(0, this.uploading[sectionType] - 1);
          this.cdr.detectChanges();
        });
      })
    ).subscribe();
  }

  private sanitizeFileName(fileName: string): string {
    return fileName.replace(/[^a-zA-Z0-9.-]/g, '_');
  }

  private getFileType(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    const typeMap: { [key: string]: string } = {
      'pdf': 'pdf',
      'doc': 'word',
      'docx': 'word',
      'txt': 'text',
      'jpg': 'image',
      'jpeg': 'image',
      'png': 'image'
    };
    return typeMap[extension || ''] || 'file';
  }

  removeDocument(sectionType: 'proyecto' | 'inmueble' | 'obra', documentId: string): void {
    this.documents[sectionType] = this.documents[sectionType].filter(doc => doc.id !== documentId);
    this.saveDocumentsToPropertyForm();
    this.cdr.detectChanges();
  }

  /**
   * Carga documentos desde el FormControl de la propiedad
   */
  private loadDocumentsFromPropertyForm(): void {
    if (!this.propertyForm) {
      this.documents = { proyecto: [], inmueble: [], obra: [] };
      return;
    }
    
    const documentsControl = this.propertyForm.get('documents');
    if (documentsControl) {
      const savedDocuments = documentsControl.value || [];
      if (Array.isArray(savedDocuments) && savedDocuments.length > 0) {
        // Organizar documentos por sección
        this.documents = {
          proyecto: savedDocuments.filter((doc: Document) => doc.sectionType === 'proyecto'),
          inmueble: savedDocuments.filter((doc: Document) => doc.sectionType === 'inmueble'),
          obra: savedDocuments.filter((doc: Document) => doc.sectionType === 'obra')
        };
      } else {
        this.documents = { proyecto: [], inmueble: [], obra: [] };
      }
    }
    this.cdr.detectChanges();
  }

  /**
   * Guarda documentos en el FormControl de la propiedad
   */
  private saveDocumentsToPropertyForm(): void {
    if (!this.propertyForm) {
      return;
    }

    const documentsControl = this.propertyForm.get('documents');
    if (documentsControl) {
      // Combinar todos los documentos de todas las secciones
      const allDocuments = [
        ...this.documents['proyecto'],
        ...this.documents['inmueble'],
        ...this.documents['obra']
      ];
      documentsControl.setValue(allDocuments, { emitEvent: false });
    }
  }

  getFileIcon(type: string): string {
    const iconMap: { [key: string]: string } = {
      'pdf': 'fa-file-pdf',
      'word': 'fa-file-word',
      'text': 'fa-file-alt',
      'image': 'fa-file-image',
      'file': 'fa-file'
    };
    return iconMap[type] || 'fa-file';
  }

  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }
}
